import { useEffect, useState } from "react";
import { Link } from "react-router";
import { storage } from "../utils/storage";
import type {
  Vehicle,
  MaintenanceRecord,
  UserMaintenanceSchedule,
  MaintenanceSchedule,
  UpcomingMaintenanceItem,
} from "../types";
import { Car, Wrench, DollarSign, Calendar, Plus } from "lucide-react";
import { differenceInDays } from "date-fns";
import { UpcomingMaintenanceWidget } from "./UpcomingMaintenanceWidget";

export function Dashboard() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [records, setRecords] = useState<MaintenanceRecord[]>([]);
  const [upcomingItems, setUpcomingItems] = useState<UpcomingMaintenanceItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [vehiclesData, recordsData] = await Promise.all([
          storage.getVehicles(),
          storage.getMaintenanceRecords(),
        ]);
        setVehicles(vehiclesData);
        setRecords(recordsData);

        // Load upcoming maintenance items across all vehicles in parallel
        const now = new Date();
        const vehicleResults = await Promise.allSettled(
          vehiclesData.map(async (vehicle) => {
            const [schedules, userSchedules] = await Promise.all([
              storage.getSchedulesForVehicle(vehicle.id),
              storage.getUserSchedulesForVehicle(vehicle.id),
            ]);
            return { vehicle, schedules, userSchedules };
          })
        );

        const allItems: UpcomingMaintenanceItem[] = [];
        for (const result of vehicleResults) {
          if (result.status !== "fulfilled") continue;
          const { vehicle, schedules, userSchedules } = result.value;
          const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;

          for (const us of userSchedules) {
            if (us.isDismissed) continue;
            const schedule = schedules.find((s) => s.id === us.scheduleId);
            if (!schedule) continue;

            let daysUntilDue: number | undefined;
            let milesUntilDue: number | undefined;
            let isOverdue = false;

            if (us.nextDueDate) {
              daysUntilDue = differenceInDays(new Date(us.nextDueDate), now);
              if (daysUntilDue < 0) isOverdue = true;
            }
            if (us.nextDueMileage) {
              milesUntilDue = us.nextDueMileage - vehicle.currentMileage;
              if (milesUntilDue < 0) isOverdue = true;
            }

            allItems.push({
              scheduleId: schedule.id,
              vehicleId: vehicle.id,
              vehicleName,
              serviceName: schedule.serviceName,
              serviceCategory: schedule.serviceCategory,
              priority: schedule.priority,
              nextDueDate: us.nextDueDate,
              nextDueMileage: us.nextDueMileage,
              daysUntilDue,
              milesUntilDue,
              isOverdue,
            });
          }
        }

        // Sort by nearest due date, take top 3
        allItems.sort((a, b) => {
          if (a.isOverdue && !b.isOverdue) return -1;
          if (!a.isOverdue && b.isOverdue) return 1;
          const aDays = a.daysUntilDue ?? Infinity;
          const bDays = b.daysUntilDue ?? Infinity;
          return aDays - bDays;
        });
        setUpcomingItems(allItems.slice(0, 3));
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const totalCost = records.reduce((sum, record) => sum + record.cost, 0);
  const recentRecords = [...records]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 5);

  const getVehicleName = (vehicleId: string) => {
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    return vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : "Unknown Vehicle";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Dashboard</h2>
        <p className="text-gray-600 mt-1">Overview of your vehicles and maintenance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Vehicles</p>
              <p className="text-3xl font-semibold text-gray-900 mt-1">{vehicles.length}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Car className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Maintenance Records</p>
              <p className="text-3xl font-semibold text-gray-900 mt-1">{records.length}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <Wrench className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Spent</p>
              <p className="text-3xl font-semibold text-gray-900 mt-1">${totalCost.toFixed(2)}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <DollarSign className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      <UpcomingMaintenanceWidget items={upcomingItems} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Your Vehicles</h3>
              <Link
                to="/vehicles"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View All
              </Link>
            </div>
          </div>
          <div className="p-6">
            {vehicles.length === 0 ? (
              <div className="text-center py-8">
                <Car className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">No vehicles added yet</p>
                <Link
                  to="/vehicles"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Your First Vehicle
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {vehicles.map((vehicle) => (
                  <div
                    key={vehicle.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </p>
                      <p className="text-sm text-gray-600">
                        {vehicle.currentMileage.toLocaleString()} miles
                      </p>
                    </div>
                    <Car className="w-5 h-5 text-gray-400" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Recent Maintenance</h3>
              <Link
                to="/maintenance"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View All
              </Link>
            </div>
          </div>
          <div className="p-6">
            {recentRecords.length === 0 ? (
              <div className="text-center py-8">
                <Wrench className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">No maintenance records yet</p>
                <Link
                  to="/maintenance"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Maintenance Record
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentRecords.map((record) => (
                  <div key={record.id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <p className="font-medium text-gray-900">{record.type}</p>
                      <p className="text-sm font-medium text-gray-900">${record.cost.toFixed(2)}</p>
                    </div>
                    <p className="text-sm text-gray-600">{getVehicleName(record.vehicleId)}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(record.date).toLocaleDateString()}
                      </span>
                      <span>{record.mileage.toLocaleString()} miles</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
